import { useEffect, useRef, useState } from 'react';
import SideBar from '../../components/SideBar/SideBar';
import TopNav from '../../components/TopNav/TopNav';
import api from '../../services/api';

// Last 100 real users (test/team accounts excluded server-side) and what
// they actually touch in the app, grouped into feature areas rather than
// raw event names. Built 2026-09-29 after a manual pull of this same data
// kept coming up in conversation about why returning users weren't
// converting to bookings or messages.
//
// Sources:
//   GET  /admin/user-activity                 -> { feature_labels, users, window_days }
//   POST /admin/user-activity/advisor-insight  -> { insight }

const REQUEST_TIMEOUT_MS = 20000;
// The advisor call is a real, non-cached LLM generation over up to 100
// users' data -- AiAnalyst.jsx's equivalent call sets no timeout at all for
// the same reason. Longer than the plain data fetch, not unbounded.
const ADVISOR_TIMEOUT_MS = 45000;

const fmt = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return '—';
  try { return date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); }
  catch { return '—'; }
};

const UserActivity = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [insight, setInsight] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [insightError, setInsightError] = useState(null);

  // Same fix as Verifications.js: only the newest request may write state,
  // so clicking "Try again" while the first load is still in flight can't
  // let a stale response overwrite a fresher one. (This does not guard
  // against writing to an unmounted page -- React 18 silently no-ops that
  // rather than erroring, so it's a non-issue, not a fixed one.)
  const reqSeq = useRef(0);
  const generatingRef = useRef(false);

  const load = async () => {
    const seq = (reqSeq.current += 1);
    setLoading(true);
    setLoadError(null);
    try {
      const res = await api.get('/admin/user-activity', { timeout: REQUEST_TIMEOUT_MS });
      if (seq !== reqSeq.current) return;
      setReport(res.data);
    } catch (err) {
      if (seq !== reqSeq.current) return;
      setLoadError(err?.response?.data?.message || err?.message || 'Request failed');
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const generateInsight = async () => {
    // React's disabled attribute only takes effect after commit, so a fast
    // double-click can fire this twice before that lands -- guard on a ref,
    // not just the generating state.
    if (generatingRef.current) return;
    generatingRef.current = true;
    setGenerating(true);
    setInsightError(null);
    setInsight(null);
    try {
      const res = await api.post('/admin/user-activity/advisor-insight', {}, { timeout: ADVISOR_TIMEOUT_MS });
      const text = res.data?.insight;
      if (!text) throw new Error('The advisor returned nothing. Try again.');
      setInsight(text);
    } catch (err) {
      setInsightError(err?.response?.data?.message || err?.message || 'Could not generate an insight.');
    } finally {
      generatingRef.current = false;
      setGenerating(false);
    }
  };

  const labels = report?.feature_labels || [];
  const users = report?.users || [];
  const ready = !loading && !loadError;

  return (
    <main>
      <TopNav />
      <div className="main-container">
        <SideBar />
        <article className="page-content">
          {/* width claimed explicitly -- see global.scss's note above
              .page-content: a section that doesn't set this drags the
              whole page under the sidebar the first time a column pushes
              it past the viewport, which an unbounded, backend-driven
              column count (labels.map) makes inevitable here eventually. */}
          <section style={{ padding: '20px 2vw', width: '100%', maxWidth: 1500, boxSizing: 'border-box' }}>
            <h1 style={{ fontSize: 28, fontWeight: 700, color: '#fff', margin: '0 0 4px' }}>User Activity</h1>
            <p style={{ color: 'rgba(255,255,255,.45)', fontSize: 13, margin: '0 0 22px' }}>
              {loading
                ? 'Loading…'
                : loadError
                  ? 'Could not load.'
                  : `${users.length} real users, most recently active first · last ${report?.window_days ?? 45} days · test/team accounts excluded`}
            </p>

            {!loading && loadError && (
              <div style={{ background: 'rgba(229,62,62,0.08)', border: '1px solid rgba(229,62,62,0.35)', borderRadius: 14, padding: 28, textAlign: 'center', color: 'rgba(255,255,255,.75)' }}>
                <div style={{ marginBottom: 12 }}>Could not load this list, so it is not the same as it being empty.</div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,.45)', marginBottom: 16 }}>{loadError}</div>
                <button type="button" onClick={load} style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.18)', color: '#fff', borderRadius: 10, padding: '8px 18px', cursor: 'pointer' }}>
                  Try again
                </button>
              </div>
            )}

            {ready && users.length === 0 && (
              <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, padding: 40, textAlign: 'center', color: 'rgba(255,255,255,.4)' }}>
                No real users matched this window — everyone recently active was a test/team account, or nobody has been active in the last {report?.window_days ?? 45} days.
              </div>
            )}

            {ready && users.length > 0 && (
              <div style={{ overflowX: 'auto', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14 }}>
                <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12.5, color: 'rgba(255,255,255,.85)' }}>
                  <thead>
                    <tr style={{ background: 'rgba(255,255,255,0.04)' }}>
                      <th style={{ position: 'sticky', left: 0, background: '#000', textAlign: 'left', padding: '10px 14px', whiteSpace: 'nowrap', color: 'rgba(255,255,255,.5)', fontWeight: 600 }}>User</th>
                      <th style={{ textAlign: 'left', padding: '10px 14px', whiteSpace: 'nowrap', color: 'rgba(255,255,255,.5)', fontWeight: 600 }}>Last active</th>
                      <th style={{ textAlign: 'right', padding: '10px 14px', whiteSpace: 'nowrap', color: 'rgba(255,255,255,.5)', fontWeight: 600 }}>Total</th>
                      {labels.map((l) => (
                        <th key={l} style={{ textAlign: 'right', padding: '10px 14px', whiteSpace: 'nowrap', color: 'rgba(255,255,255,.5)', fontWeight: 600 }}>{l}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id || u._id || u.email} style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                        <td style={{ position: 'sticky', left: 0, background: '#000', padding: '10px 14px', whiteSpace: 'nowrap' }}>
                          <div style={{ color: '#fff', fontWeight: 600 }}>{u.name}</div>
                          <div style={{ color: 'rgba(255,255,255,.35)', fontSize: 11 }}>{u.email}</div>
                        </td>
                        <td style={{ padding: '10px 14px', whiteSpace: 'nowrap', color: 'rgba(255,255,255,.6)' }}>{fmt(u.last_active)}</td>
                        <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 700, color: '#fff' }}>{typeof u.total_touches === 'number' ? u.total_touches : '—'}</td>
                        {labels.map((l) => {
                          // A feature key only ever exists when it was touched
                          // at least once (see userActivityStats.js), but read
                          // it as "present or not" rather than truthy/falsy so
                          // a genuine 0 is never mistaken for no data. Guard
                          // the value itself is a number before rendering it --
                          // a non-primitive here would otherwise blank the
                          // whole page, not just this cell.
                          const has = u.touches && Object.prototype.hasOwnProperty.call(u.touches, l) && typeof u.touches[l] === 'number';
                          return (
                            <td key={l} style={{ padding: '10px 14px', textAlign: 'right', color: has ? 'rgba(255,255,255,.85)' : 'rgba(255,255,255,.2)' }}>
                              {has ? u.touches[l] : '—'}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Advisor: turns the table above into a ranked read, generated on
                demand rather than on every load (it's a real model call). */}
            <div style={{ marginTop: 28, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ color: '#fff', fontSize: 15, fontWeight: 700 }}>Advisor read</div>
                  <div style={{ color: 'rgba(255,255,255,.4)', fontSize: 12, marginTop: 2 }}>Generated from the table above, on demand.</div>
                </div>
                <button
                  type="button"
                  onClick={generateInsight}
                  disabled={generating || !ready || users.length === 0}
                  style={{ borderRadius: 10, padding: '10px 18px', fontSize: 13, fontWeight: 700, cursor: generating ? 'default' : 'pointer', border: 'none', background: 'linear-gradient(135deg,#0A3FD6,#7229F5)', color: '#fff', opacity: generating || !ready || users.length === 0 ? 0.6 : 1 }}
                >
                  {generating ? 'Generating…' : 'Generate'}
                </button>
              </div>

              {insightError && (
                <div style={{ marginTop: 16, color: '#f87171', fontSize: 13 }}>{insightError}</div>
              )}

              {insight && (
                <div style={{ marginTop: 16, color: 'rgba(255,255,255,.85)', fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                  {insight}
                </div>
              )}
            </div>
          </section>
        </article>
      </div>
    </main>
  );
};

export default UserActivity;
